import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "./PreviewConvertPage.scss";
import {
  showSuccessToast,
  showErrorToast,
} from "../components/toastComponent/ToastComponent";
import { getVeevaPdfFile, createPdfDeck } from "../services/apis";
import { ReactComponent as DashboardImage } from "../images/home-icon.svg";
import ModuleStepper from "../components/ModuleStepper/ModuleStepper";
import InfoIcon from "../images/information--square.svg";
import CircularProgressBar from "../utils/CircularProgressBar";
import GreenTickMark from "../images/Final-Status.svg";
import NotApprovedImage from "../images/not-approved-image.svg";
import ConfirmationModal from "../components/template/confirmationTemplate";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const HomeIcon = () => <DashboardImage width="20" height="20" />;

// System-generated name function
const generateDeckName = (baseName = "pdf_eda") => {
  // Sanitize input
  const sanitizedBase =
    baseName
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")
      .substring(0, 40) || "pdf_eda";

  // Short random suffix (UI-side only)
  const suffix = Math.random().toString(36).substring(2, 8);

  return `${sanitizedBase}_${suffix}`;
};

const PreviewConvertPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [edetailName, setEdetailName] = useState("eDetail_01");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [visiblePages, setVisiblePages] = useState(0)
  const [selectedPage, setSelectedPage] = useState(1);
  const [pageOrder, setPageOrder] = useState([]);
  const [currentStep] = useState(2);
  const [loading, setLoading] = useState(true);
  const [convertLoading, setConvertLoading] = useState(false);
  const pdfUrlRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);
  const [productName, setProductName] = useState("");
  const [countryName, setCountryName] = useState("");
  const [indicationName, setIndicationName] = useState("");
  const [deckName, setDeckName] = useState(generateDeckName("pdf_eda"));
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const previewRef = useRef(null);
  const [disableConvertToHtml, setDisableConvertToHtml] = useState(false)

  const steps = ["Search in PromoMats", "Preview & Convert to HTML", "Publish HTML"];
  const listRef = useRef(null);
  const AUTO_LOAD_BATCH = 3;

  useEffect(() => {
    // Get PDF docId from location state and fetch the PDF file
    if (location.state && location.state.pdfDocId) {
      setEdetailName(location.state.edetailName || "eDetail_01");
      if (location.state.pdf) {
        setPdfData(location.state.pdf);
      }
      // Use display names if provided, otherwise use IDs
      if (location.state.displayNames) {
        setProductName(location.state.displayNames.product || "");
        setCountryName(location.state.displayNames.country || "");
        setIndicationName(location.state.displayNames.indication || "");
      } else if (location.state.pdf) {
        // Fallback to IDs if display names not provided
        const pdf = location.state.pdf;
        setProductName(pdf.docProduct?.[0] || "");
        setCountryName(pdf.docCountry?.[0] || "");
        setIndicationName(pdf.docIndication?.[0] || "");
      }
      setLoading(true);

      console.log("Fetching PDF with docId:", location.state.pdfDocId);

      // Get version info from location state or use defaults
      const minorVersion =
        location.state.minorVersion ??
        location.state.pdf?.docMinorVersionNumber ??
        0;
      const majorVersion =
        location.state.majorVersion ??
        location.state.pdf?.docMajorVersionNumber ??
        1;

      // Fetch PDF file using API with version parameters
      getVeevaPdfFile(
        location.state.pdfDocId,
        minorVersion,
        majorVersion,
        (response, err, errorCode) => {
          setLoading(false);
          if (err) {
            console.error("Error fetching PDF file:", {
              err,
              errorCode,
              docId: location.state.pdfDocId,
              responseUrl: err?.response?.config?.url,
            });
            showErrorToast(
              `Failed to load PDF file: ${errorCode === 404 ? "PDF not found" : "Server error"
              }`
            );
            // Don't navigate away, let user try again or go back
          } else if (response && response.data) {
            // API now returns JSON with fileUrl instead of blob
            let responseData;
            try {
              // Check if response.data is already an object or needs parsing
              if (typeof response.data === "string") {
                responseData = JSON.parse(response.data);
              } else {
                responseData = response.data;
              }
            } catch (parseError) {
              console.error("Error parsing response:", parseError);
              showErrorToast("Invalid response format from server");
              return;
            }

            if (responseData?.response?.fileUrl) {
              // Use the fileUrl directly (no blob conversion needed)
              const url = responseData.response.fileUrl;
              // No need to cleanup blob URLs since we're using a regular URL
              pdfUrlRef.current = url;
              setPdfUrl(url);
              console.log("PDF loaded successfully, URL:", url);
            } else {
              console.error("No fileUrl in response:", responseData);
              showErrorToast("Invalid response format from server");
            }
          } else {
            console.error("Invalid response from PDF API:", response);
            showErrorToast("Invalid response from server");
          }
        }
      );
    } else {
      // If no state, redirect back to import page
      console.warn("No pdfDocId in location.state, redirecting to import page");
      navigate("/import-pdf");
    }

    // Cleanup on unmount (only needed for blob URLs, not regular URLs)
    return () => {
      if (pdfUrlRef.current && pdfUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.pdfDocId, navigate]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || !totalPages) return;

    const hasScroll = el.scrollHeight > el.clientHeight;

    if (!hasScroll && visiblePages < totalPages) {
      setVisiblePages((prev) =>
        Math.min(prev + AUTO_LOAD_BATCH, totalPages)
      );
    }
  }, [visiblePages, totalPages]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setTotalPages(numPages);

    setPageOrder((prevOrder) => {
      if (prevOrder.length === 0 && numPages > 0) {
        return Array.from({ length: numPages }, (_, i) => i + 1);
      }
      return prevOrder;
    });

    setSelectedPage((prev) => {
      if (!prev || prev > numPages) return 1;
      return prev;
    });

    setVisiblePages((prev) => {
      if (prev > 0) {
        return Math.min(prev, numPages);
      }

      return Math.min(AUTO_LOAD_BATCH, numPages);
    });
  };


  const onDocumentLoadError = (error) => {
    console.error("Error loading PDF:", error);
    showErrorToast("Failed to load PDF file");
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(pageOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setPageOrder(items);
  };

  const handleThumbnailClick = (pageNumber) => {
    setSelectedPage(pageNumber);
    if (previewRef.current) {
      previewRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleConvertToHTML = () => {
    // Build request payload from available data
    if (!pdfData) {
      showErrorToast("PDF data not available");
      return;
    }

    // Validate name
    if (deckName.trim().length < 3) {
      showErrorToast("Please use minimum 3 characters for e-Detail name");
      return;
    }
    if (deckName.trim().length > 30) {
      showErrorToast("e-Detail name exceeded the maximum length");
      return;
    }

    // Get product and country IDs from filters or resolved IDs from previous screen
    // Note: We avoid using pdfData.docProduct[0] directly as it may be an external ID (e.g. Veeva ID)
    // which causes validation errors on the backend.
    const productId =
      location.state?.filters?.product?._id ||
      location.state?.filters?.product?.id ||
      location.state?.resolvedIds?.brandId;

    const countryId =
      location.state?.filters?.country?._id ||
      location.state?.filters?.country?.id ||
      location.state?.resolvedIds?.marketId;

    if (!productId || !countryId) {
      showErrorToast("Product and Country are required");
      return;
    }

    // Build the request payload
    const payload = {
      name: deckName || generateDeckName("pdf_eda"),
      brand: productId,
      market: countryId,
      dimension: null,
      device: null,
      campaign: null,
      type: 2,
      desc: "",
      slides: [],
      thumbnail: "",
      tags: ["PDF_TO_HTML"],
      docNumber: pdfData.docDocumentNumber || "",
      docId: pdfData.docId,
      majorVersion: pdfData.docMajorVersionNumber ?? 1,
      minorVersion: pdfData.docMinorVersionNumber ?? 0,
    };

    console.log("Converting PDF to HTML with payload:", payload);

    // Set loading state
    setConvertLoading(true);
    setDisableConvertToHtml(true)

    // Call the API
    createPdfDeck(payload, (response, err, errorCode) => {
      setConvertLoading(false);
      if (err) {
        console.error("Error converting PDF to HTML:", {
          err,
          errorCode,
          response: err?.response?.data,
        });
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          `Failed to convert PDF: ${errorCode === 400
            ? "Bad Request"
            : errorCode === 404
              ? "Not Found"
              : "Server Error"
          }`;
        showErrorToast(errorMessage);
        setTimeout(() => {
          setDisableConvertToHtml(false)
          navigate("/import-pdf",
            {
              state: {
                selectedPdf: pdfData || location.state?.pdf,
                filters: {
                  product: location.state?.filters?.product,
                  country: location.state?.filters?.country,
                  indication: location.state?.filters?.indication,
                },
              },
            }
          );
        }, 2000);
      } else if (response && response.data) {
        // Show success message from API response
        const successMessage = "HTML Conversion is in progress. An email notification will be sent to you once the PDF has been converted.";

        // response.data?.response?.message ||
        // response.data?.message ||
        showSuccessToast(successMessage, undefined, undefined, 10000);

        // Redirect to dashboard after showing toast
        setTimeout(() => {
          setDisableConvertToHtml(false)
          navigate("/");
        }, 2000);
      } else {
        showErrorToast("Invalid response from server");
      }
    });
  };

  const handleBackToSelection = () => {
    // Navigate back to ImportPDFPage with PDF data to preserve selection
    navigate("/import-pdf", {
      state: {
        selectedPdf: pdfData || location.state?.pdf,
        filters: {
          product: location.state?.filters?.product,
          country: location.state?.filters?.country,
          indication: location.state?.filters?.indication,
        },
      },
    });
  };

  return (
    <div className="preview-convert-page">
      {/* Loader for convert operation */}
      <CircularProgressBar isloading={convertLoading} />
      {/* Header */}
      <div className="import-pdf-header">
        <div className="header-left">
          <div className="home-icon" onClick={() => navigate("/")}>
            <div className="home-icon-wrapper">
              <HomeIcon />
            </div>
            <div className="home-icon-separator"></div>
            <div className="home-icon-text">
              <div className="header-title">Create e-Detail</div>
              <div className="header-subtitle">{deckName}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <ModuleStepper currentStep={currentStep} steps={steps} />

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-bar-content">
          <div className="status-left">
            <img src={location?.state?.pdf?.docStatus === "Approved for Use"? GreenTickMark : NotApprovedImage } alt="info" className="info-icon" /> {/**/}
            <span className="status-text">{location?.state?.pdf?.docDocumentNumber}</span>
            <div className="status-right">
              <span className="status-field">Name:  <span className="status-value">{deckName}</span></span>
              <span className="status-field">
                Product:  <span className="status-value">{productName || "-"}</span>
              </span>
              <span className="status-field">
                Country: <span className="status-value">{countryName || "-"}</span>
              </span>
              {indicationName && (
                <span className="status-field">
                  Indication:  <span className="status-value">{indicationName || "-"}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="preview-convert-content">
        {loading && (
          <div className="loading-overlay">
            <div className="loading-text">Loading PDF...</div>
          </div>
        )}
        <div className="content-wrapper">
          {/* Left Sidebar - Thumbnails */}
          <div className="thumbnails-sidebar">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="thumbnails">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={(node) => {
                      listRef.current = node;
                      provided.innerRef(node);
                    }}
                    className="thumbnails-list"
                    onScroll={(e) => {
                      const { scrollTop, scrollHeight, clientHeight } = e.target;
                      if (scrollTop + clientHeight >= scrollHeight - 300) {
                        setVisiblePages((prev) =>
                          Math.min(prev + AUTO_LOAD_BATCH, totalPages)
                        );
                      }
                    }}
                  >
                    {pdfUrl && (
                      <Document
                        file={pdfUrl}
                        loading={
                          <div className="thumbnail-loading">
                            Loading...
                          </div>
                        }
                        error={
                          <div className="thumbnail-error">Error</div>
                        }
                      >
                        {(visiblePages > 0
                          ? Array.from({ length: visiblePages }, (_, i) => i + 1)
                          : []
                        ).map((pageNum, index) => (
                          <Draggable
                            key={pageNum}
                            draggableId={`page-${pageNum}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`thumbnail-item ${selectedPage === pageNum ? "selected" : ""
                                  } ${snapshot.isDragging ? "dragging" : ""}`}
                                onClick={() => handleThumbnailClick(pageNum)}
                              >
                                <div className="thumbnail-number">{pageNum + " ."}</div>
                                <div className="thumbnail-preview">
                                  <Page
                                    pageNumber={pageNum}
                                    width={120}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                  />
                                </div>
                                <div className="thumbnail-label">
                                  Name: <span className="thumbnail-label-name">Slide {pageNum}</span>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Document>
                    )}
                  </div>

                )}
              </Droppable>
            </DragDropContext>
          </div>

          {/* Right Panel - Preview */}
          <div className="preview-panel">
            <div className="preview-content">
              {pdfUrl && selectedPage ? (
                <div className="pdf-preview-wrapper" ref={previewRef}>
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={<div className="pdf-loading">Loading PDF...</div>}
                  >
                    <Page
                      pageNumber={selectedPage}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="pdf-page"
                      width={850}
                      loading={
                        <div className="page-loading">
                          Loading page {selectedPage}...
                        </div>
                      }
                      error={
                        <div className="page-error">
                          Error loading page {selectedPage}
                        </div>
                      }
                    />
                  </Document>
                </div>
              ) : (
                <div className="preview-placeholder">
                  <div className="placeholder-text">
                    {loading ? "Loading PDF..." : "Select a page to preview"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="preview-convert-footer">
          <button
            className="btn-back-to-selection"
            onClick={() => { setShowConfirmModal(true) }}
          >
            CANCEL
          </button>
          <div className="footer-right-buttons">
            <button className="btn-cancel" onClick={handleBackToSelection}>
              BACK
            </button>
            <button
              className="btn-convert"
              onClick={handleConvertToHTML}
              disabled={disableConvertToHtml}
              style={{
                opacity: disableConvertToHtml ? 0.6 : 1,
                cursor: disableConvertToHtml ? "not-allowed" : "pointer",
              }}
            >
              {disableConvertToHtml ? "CONVERTING..." : "CONVERT TO HTML"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={() => {
          navigate("/");
        }}
        title="ARE YOU SURE GO BACK TO THE DASHBOARD"
        message="If you go back to the dashboard, you will exit the flow and return to the dashboard."
        confirmText="YES"
        cancelText="NO"
      />
    </div>
  );
};

export default PreviewConvertPage;